// @flow @format
import Dexie from 'dexie';
import type {Metafile, Repository} from 'lib/cloner-api';

export const DOWNLOAD_PAUSING_STATUS = 'pausing';
export const DOWNLOAD_PAUSED_STATUS = 'paused';
export const DOWNLOAD_SETUP_STATUS = 'setup';
export const DOWNLOAD_RUNNING_STATUS = 'running';
export const DOWNLOAD_COMPLETED_STATUS = 'completed';
export const DOWNLOAD_FAILED_STATUS = 'failed';
export const DOWNLOAD_RETRY_STATUS = 'retry';

export type DownloadRecordStatus =
	| 'pausing'
	| 'paused'
	| 'setup'
	| 'running'
	| 'completed'
	| 'failed'
	| 'retry';

export type DownloadRecord = {
	id: string,
	hash: string,
	owner: string,
	parent: string,
	repositoryId: string,
	machineId: string,
	filename: string,
	isFile: boolean,
	numBlocks: number,
	offset: number, // only used for folders
	hasFile: boolean, // true if has a file created
	initialized: boolean, // true if has fetched file blocks metadata or needs setup
	includeDeleted: boolean,
	blockStatus: boolean[], // keeps track of the downloaded blocks
	size: number, // size in bytes of the file
	downloaded: number, // number of bytes downloaded of the file
	diskSize: number,
	createdAt: Date,
	updatedAt: Date,
	status: DownloadRecordStatus,
};

const latestVersion = 4611686018427388000;

function nameWithVersion(metafile: Metafile): string {
	if (metafile.VersionFin === latestVersion) {
		return metafile.Name;
	}

	let index = metafile.Name.lastIndexOf('.');
	if (index === -1) {
		return `${metafile.Name}.${metafile.VersionFin}`;
	}

	const name = metafile.Name.substr(0, index);
	const ext = metafile.Name.substr(index + 1);

	return `${name}.${metafile.VersionFin}.${ext}`;
}

// Store stores keeps track of files download.
class Store {
	_db: Dexie;

	constructor(key: string) {
		this._db = new Dexie(key);
		this._db.version(1).stores({
			downloads: '&id,status,createdAt',
		});
	}

	getDownload(id: string): Promise<DownloadRecord> {
		const db = this._db;
		return db.transaction('rw', db.table('downloads'), function() {
			return db.table('downloads').get(id);
		});
	}

	createDownload(
		repository: Repository,
		metafile: Metafile,
		includeDeleted: boolean,
	): Promise<DownloadRecord> {
		const db = this._db;
		return db.transaction('rw', db.table('downloads'), function() {
			const now = new Date();
			const record: DownloadRecord = {
				id: metafile.Id,
				owner: repository.owner,
				machineId: repository.mid,
				repositoryId: repository.id,
				parent: metafile.Parent,
				isFile: metafile.Type === 1,
				hash: metafile.Hash,
				filename: nameWithVersion(metafile),
				size: metafile.Size,
				offset: 0,
				includeDeleted: includeDeleted,
				downloaded: 0,
				numBlocks: 0,
				diskSize: 0,
				blockStatus: [],
				createdAt: now,
				updatedAt: now,
				initialized: false,
				hasFile: false,
				status: DOWNLOAD_PAUSED_STATUS,
			};

			return db.table('downloads').add(record).then(() => record);
		});
	}

	updateDownload(record: DownloadRecord): Promise<number> {
		const db = this._db;
		return db.transaction('rw', db.table('downloads'), function() {
			return db.table('downloads').update(record.id, {
				status: record.status,
				hasFile: record.hasFile,
				initialized: record.initialized,
				offset: record.offset,
				downloaded: record.downloaded,
				blockStatus: record.blockStatus,
				numBlocks: record.numBlocks,
				updatedAt: record.updatedAt,
			});
		});
	}

	removeDownload(id: string): Promise<void> {
		const db = this._db;
		return db.transaction('rw', db.table('downloads'), function() {
			return db.table('downloads').delete(id);
		});
	}

	allDownloads(
		...statuses: (DownloadRecordStatus | number)[]
	): Promise<DownloadRecord[]> {
		const db = this._db;
		return db.transaction('r', db.table('downloads'), function() {
			if (statuses.length > 0) {
				return db
					.table('downloads')
					.where('status')
					.anyOf(...statuses)
					.toArray();
			}
			return db.table('downloads').toArray();
		});
	}
}

export default Store;



// WEBPACK FOOTER //
// ./src/lib/downloader/store.js